import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import { Table } from 'reactstrap';
import { orderBy } from 'lodash';
import { object, array, number, bool } from 'prop-types';
import StatisticsTableGroup from './StatisticsTableGroup';
import { StatisticsTableField } from './index';
import StatisticsGoalsTableItem from './StatisticsGoalsTableItem';
import StatisticsGoalsTotal from './StatisticsGoalsTotal';
import { getLinkToConversationTable, removeEmptyStats, isMaster } from './statisticsHelpers';
import StickyTable from '../../hoc/StickyTable';
import WithHover from '../../hoc/WithHover';
import { isString, getToFixedParams, _toFixed, currencyToSign } from '../../utils/utils';
import { FMessage } from '../../hoc/IntlComponents';
import { LocalStorage } from '../../utils/utils';
import EditingModal from '../../modals/EditingModal/EditingModal';
import ActionModal from '../../modals/ActionModal/ActionModal';

const HIDDEN_COLUMNS_STORAGE_KEY = 'statistics_table_hidden_columns';

class StatisticsGoalsTable extends Component {
  static propTypes = {
    config: array.isRequired,
    stats: array.isRequired,
    total: object.isRequired,
    offers: array.isRequired,
    currency: number.isRequired,
    dict: object.isRequired,
    theme: number.isRequired,
    hasTableEditingModalOpen: bool,
  };

  static defaultProps = {
    config: [],
    stats: [],
    total: {},
    offers: [],
    currency: 0,
    dict: {},
    theme: 1,
  };

  constructor(props) {
    super(props);
    const { stats, currency, tab, config } = props;

    const savedHiddenColumns = this.getSavedHiddenColumns(tab);

    this.state = {
      sort: {
        field: 127,
        order: -1,
        inverse: false,
      },
      ignored: config
        .map(({ id, type }) => (type === 'pixel_id' || type === 'sub_data' ? id : null))
        .filter(Boolean),

      stats: props.stats ? removeEmptyStats({ stats, currency, tab, config }) : [],
      config,
      hiddenColumns: savedHiddenColumns,
      pendingHiddenColumns: savedHiddenColumns,
    };
  }

  mergeConfigWithStorage = (config) => {
    return config;
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { config, stats, tab, currency } = nextProps;

    this.setState(() => ({
      ignored: config
        .map(({ id, type }) => (type === 'pixel_id' || type === 'sub_data' ? id : null))
        .filter(Boolean),
      stats: removeEmptyStats({ stats, currency, tab, config }),
      config: this.mergeConfigWithStorage(config),
    }));
  }

  componentDidMount() {
    this.updatePagesCount();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.stats !== this.state.stats) {
      this.updatePagesCount();
    }
  }

  updatePagesCount = () => {
    const { stats } = this.state;
    const { limit, setPagesCount } = this.props;

    if (!stats.length) {
      setPagesCount();
      return;
    }

    const pageCount = Math.ceil(stats.length / limit);
    setPagesCount(pageCount);
  };

  makeFields = () => {
    const { currency } = this.props;
    const { config, ignored, hiddenColumns } = this.state;

    return config
      .filter((cfg) => !hiddenColumns.includes(cfg.id))
      .map((cfg) =>
        ignored.includes(cfg.id)
          ? null
          : {
              id: cfg.id,
              title: cfg.title || '',
              is_sortable: cfg.sortable || false,
              type: cfg.type,
              inverse: false,
              label: cfg.label || '',
              postfix: cfg.postfix[currency],
              isRendering: cfg.isRendering,
            },
      )
      .filter(Boolean);
  };

  getStorageKey = (tab = null) => {
    const currentTab = tab || this.props.tab;
    if (!currentTab) {
      return HIDDEN_COLUMNS_STORAGE_KEY;
    }
    return `${HIDDEN_COLUMNS_STORAGE_KEY}_${currentTab}`;
  };

  getSavedHiddenColumns = (tab = null) => {
    try {
      const storageKey = this.getStorageKey(tab);
      const saved = LocalStorage.get(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading hidden columns from LocalStorage:', error);
      return [];
    }
  };

  saveHiddenColumnsToStorage = (hiddenColumns, tab = null) => {
    try {
      const storageKey = this.getStorageKey(tab);
      LocalStorage.set(storageKey, JSON.stringify(hiddenColumns));
    } catch (error) {
      console.error('Error saving hidden columns to LocalStorage:', error);
    }
  };

  mergeConfigWithStorage = (config) => {
    return config;
  };

  getAllGroups = () => {
    const { config, customStrings = {} } = this.props;
    const { group: customTitles } = customStrings;

    const groups = new Set();

    config.forEach((column) => {
      if (column.isRendering !== false) {
        const groupId = column.group || '__EMPTY__';
        let groupTitle = groupId;
        if (customTitles && customTitles[groupId]) {
          groupTitle = customTitles[groupId];
        }

        groups.add(groupTitle);
      }
    });

    return Array.from(groups);
  };

  togglePendingColumnVisibility = (columnId) => {
    this.setState((prevState) => {
      const pendingHiddenColumns = [...prevState.pendingHiddenColumns];
      const columnIndex = pendingHiddenColumns.indexOf(columnId);

      if (columnIndex > -1) {
        pendingHiddenColumns.splice(columnIndex, 1);
      } else {
        pendingHiddenColumns.push(columnId);
      }

      return { pendingHiddenColumns };
    });
  };

  applyColumnChanges = () => {
    const { pendingHiddenColumns } = this.state;
    const { tab, handleHasEditingModalToggle } = this.props;

    this.setState(
      {
        hiddenColumns: [...pendingHiddenColumns],
      },
      () => {
        this.saveHiddenColumnsToStorage(pendingHiddenColumns, tab);
        handleHasEditingModalToggle();
      },
    );
  };

  handleCancelChanges = () => {
    this.setState({ pendingHiddenColumns: [...this.state.hiddenColumns] });
    this.props.handleHasEditingModalToggle();
  };

  isColumnHidden = (columnId) => {
    return this.state.hiddenColumns.includes(columnId);
  };

  isPendingColumnHidden = (columnId) => {
    return this.state.pendingHiddenColumns?.includes(columnId);
  };

  getOffer = (id) => this.props.offers.find((offer) => offer.id === id);

  getOrderedStats = () => {
    const { sort, stats } = this.state;
    const { currency } = this.props;
    const direction = sort.inverse ? sort.order * -1 : sort.order * 1;

    if (stats.length > 0 && stats[0][sort.field] !== undefined) {
      return orderBy(stats, `${sort.field}[${currency}]`, direction > 0 ? 'asc' : 'desc');
    }
    return stats;
  };

  getFormattedValue = (value, type, statId, stat, isTotal, isSubData) => {
    const { dict, offers, tab, timeZones, currency, isMember } = this.props;
    const { config } = this.state;
    const { numFormat, strFormat } = getToFixedParams(config, statId);
    switch (type) {
      case 'int':
        const intValue = numeral(value).format('0,0');
        if (isSubData) {
          return statId === 30 ? <span className="gray">{intValue}</span> : intValue;
        }
        if ([127, 27, 28, 29, 30].find((id) => id === statId)) {
          return getLinkToConversationTable({
            value: intValue,
            statId,
            tab,
            stat,
            config,
            timeZones,
            isTotal,
            isSubData,
            currency,
          });
        }
        return intValue;
      case 'float':
        return numeral(_toFixed(value, numFormat)).format(strFormat);
      case 'money':
        const currencyString = currency === 0 ? 'rub' : currency === 1 ? 'usd' : 'eur';
        return (
          <>
            {value % 1 === 0 ? value : numeral(_toFixed(value, numFormat)).format(strFormat)}&nbsp;
            {currencyToSign(currencyString)}
          </>
        );
      case 'string':
        return value.toString();
      case 'pixel_id':
        let pixel;

        if (dict['pixels'] !== undefined) {
          pixel = dict['pixels'].find((pixel) => pixel.id === value);
        }
        if (!pixel && offers && offers.pixels) {
          pixel = offers.pixels.find(({ id }) => id === value);
        }

        return pixel && isString(pixel.title) ? pixel.title : value;
      case 'sub_data':
        if (Array.isArray(value)) {
          return value.map((val) => this.normalizeData(val, { isSubData: true }));
        }

        return value;
      case 'offer_id':
        let offer;

        if (dict['offers'] !== undefined) {
          offer = dict['offers'].find((offer) => offer.id === value);
        }

        if (!offer && offers) {
          offer = offers.find((offer) => offer.id === value);
        }

        return offer && isString(offer.title) ? (
          !isMember ? (
            <Link to={`/offers/${offer.id}`} target="_blank">
              {offer.title}
            </Link>
          ) : (
            offer.title
          )
        ) : (
          value
        );
      default:
        return value;
    }
  };

  normalizeData = (stat, { isSubData = false, isTotal }) => {
    const { currency } = this.props;
    const { config } = this.state;
    const ignoredBySubData = [24, 25, 26, 123, 124, 36];

    return config.reduce((normalized, { id, type, isRendering }) => {
      if (type === 'sub_data') {
        const formatted = this.getFormattedValue(stat[id], type, id, stat, isTotal);
        return { ...normalized, [id]: formatted };
      }

      if (stat[id] && isRendering !== false) {
        const formatted =
          isSubData && ignoredBySubData.includes(id)
            ? ''
            : this.getFormattedValue(stat[id][currency], type, id, stat, isTotal, isSubData);
        return { ...normalized, [id]: formatted };
      }
      return normalized;
    }, {});
  };

  handleSort = (field, inverse, e) => {
    e.preventDefault();

    const { sort } = this.state;
    let order = field === sort.field ? sort.order * -1 : 1;

    this.setState(() => ({
      sort: {
        field,
        order,
        inverse,
      },
    }));
  };

  renderTableGroups = () => {
    const { ignored, config } = this.state;
    const { customStrings = {}, currency, currencies } = this.props;
    const { group: customTitles } = customStrings;

    const groups = config.reduce((fields, item, i) => {
      const { group } = item;

      if (item.isRendering === false) return fields;

      const key = group ? group : `_EMPTY_${i}`;

      if (ignored.includes(item.id)) {
        return fields;
      }

      return { ...fields, [key]: fields[group] ? fields[group] + 1 : 1 };
    }, {});

    return Object.keys(groups).map((title) => {
      let formattedTitle = title;
      if (customTitles && customTitles[title]) {
        formattedTitle = customTitles[title];
      }
      return (
        <StatisticsTableGroup
          key={title}
          colSpan={groups[title]}
          title={formattedTitle}
          currency={currencies[currency]}
        />
      );
    });
  };

  renderTableFields = () => {
    const fields = this.makeFields();
    const { template } = this.props;
    const { sort } = this.state;

    return fields.map((field) => (
      <StatisticsTableField
        key={field.id}
        order={sort.order}
        onSort={this.handleSort}
        sorted={sort.field === field.id}
        template={template}
        {...field}
      />
    ));
  };

  renderStats = () => {
    const { ignored, config } = this.state;
    const { page, dict, offers } = this.props;
    const stats = this.splitStats(this.getOrderedStats());
    const [cfg] = config.filter(({ type }) => type === 'offer_id');
    if (!stats[page]) {
      return null;
    }
    if (cfg && cfg.id) {
      return stats[page].map((stat, id) => {
        const offer = stat[cfg.id] && stat[cfg.id][0] ? this.getOffer(stat[cfg.id][0]) : {};

        return (
          <StatisticsGoalsTableItem
            key={id}
            offer={offer}
            config={config}
            ignored={ignored}
            dict={dict}
            offers={offers}
            data={this.normalizeData(stat, {})}
          />
        );
      });
    }
  };

  renderTotal = () => {
    const { ignored, stats } = this.state;
    const { total, config } = this.props;
    if (!stats.length) {
      return null;
    }

    const totalStats = Object.keys(total).reduce((totalStats, key) => {
      if (ignored.includes(parseInt(key, 10))) {
        return totalStats;
      } else {
        return { ...totalStats, [key]: total[key] };
      }
    }, {});

    return (
      <StatisticsGoalsTotal
        config={config}
        stats={this.normalizeData(totalStats, { isTotal: true })}
      />
    );
  };

  splitStats = (stats = []) => {
    const chunked = [];
    const { limit } = this.props;

    for (let i = 0, len = stats.length; i < len; i += limit) {
      chunked.push(stats.slice(i, i + limit));
    }
    return chunked;
  };

  renderColumnControls = () => {
    const { config, ignored } = this.state;
    const editingConfig = config.filter((cfg) => !ignored.includes(cfg.id));

    const {
      customStrings = {},
      hasTableEditingModalOpen,
      hasActionModalOpen,
      handleHasActionModalToggle,
      isMember,
      isSharing,
      setSharingState,
      showAlert,
      requestParams,
      theme,
      onRefresh,
      handleHasEditingModalToggle,
      handleHasExportModalToggle,
    } = this.props;
    const { group: customTitles } = customStrings;
    const allGroups = this.getAllGroups();

    return (
      <>
        <EditingModal
          config={editingConfig}
          customTitles={customTitles}
          hasTableEditingModalOpen={hasTableEditingModalOpen}
          allGroups={allGroups}
          isPendingColumnHidden={this.isPendingColumnHidden}
          togglePendingColumnVisibility={this.togglePendingColumnVisibility}
          handleCancelChanges={this.handleCancelChanges}
          applyColumnChanges={this.applyColumnChanges}
        />

        <ActionModal
          isOpen={hasActionModalOpen}
          isMember={isMember}
          isSharing={isSharing}
          setSharingState={setSharingState}
          showAlert={showAlert}
          requestParams={requestParams}
          theme={theme}
          handleRefresh={onRefresh}
          handleHasEditingModalToggle={handleHasEditingModalToggle}
          handleHasExportModalToggle={handleHasExportModalToggle}
          handleClose={handleHasActionModalToggle}
        />
      </>
    );
  };

  renderEmptyData = () => {
    const { config, loading, isMounted } = this.props;
    const { hiddenColumns } = this.state;

    const visibleColumnsCount = config.filter((cfg) => !hiddenColumns.includes(cfg.id)).length;

    if (visibleColumnsCount > 0) {
      return (
        <tbody>
          <tr>
            <td className="empty-stats" colSpan={visibleColumnsCount}>
              {loading ? (
                <FMessage id="statistics.table.loading" />
              ) : !isMounted ? null : (
                <FMessage id="statistics.table.no-data" />
              )}
            </td>
          </tr>
        </tbody>
      );
    }
  };

  render() {
    const { stats } = this.state;

    return (
      <>
        <StickyTable>
          <Table
            className="statistics-table sticky-table goals-table inverse"
            tag={WithHover}
            responsive
            hover
          >
            <thead>
              <tr>{this.renderTableGroups()}</tr>
              <tr className="table-fields">{this.renderTableFields()}</tr>
            </thead>
            {stats.length > 0 ? (
              <Fragment>
                {this.renderStats()}
                <tfoot>{this.renderTotal()}</tfoot>
              </Fragment>
            ) : (
              this.renderEmptyData()
            )}
          </Table>
        </StickyTable>
        {this.renderColumnControls()}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  timeZones: state.app.globalSettings.timezones,
  dict: !isMaster() ? state.statistics.dict : state.slaves.dict,
  offers: !isMaster() ? state.webmaster.offers : state.slaves.offers,
  currencies: state.app.globalSettings.currencies,
  theme: state.webmaster.profile.theme,
});

export default connect(mapStateToProps)(StatisticsGoalsTable);
